import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api, type IntegrationRequest, type RequestMetadata } from '@/services/api';
import { useAdminCabinetUrl } from '@/context/AdminCabinetUrlContext';
import { Loader2, CheckCircle, XCircle, RefreshCw, Plug, MoreVertical, ExternalLink } from 'lucide-react';

const INTEGRATION_LABELS: Record<string, string> = {
  yandex_eda: 'Яндекс.Еда',
  wolt: 'Wolt',
  halyk_market: 'Халык маркет',
  glovo: 'Glovo',
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'Ожидает',
  approved: 'Одобрено',
  rejected: 'Отклонено',
};

const STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive'> = {
  pending: 'secondary',
  approved: 'default',
  rejected: 'destructive',
};

function formatDate(s: string) {
  return new Date(s).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function parseMetadata(r: IntegrationRequest): RequestMetadata | null {
  const raw = r.request_metadata;
  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as RequestMetadata;
    } catch {
      return null;
    }
  }
  return raw as RequestMetadata;
}

export function Dashboard() {
  const { url: adminCabinetUrl, buildShopUrl } = useAdminCabinetUrl();
  const ADMIN_CABINET_URL = adminCabinetUrl;
  const shopUrl = (storeId: string, path: string) => buildShopUrl(storeId, path) || '#';
  const [requests, setRequests] = useState<IntegrationRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filterType, setFilterType] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');

  const loadRequests = async () => {
    setIsLoading(true);
    try {
      const { requests: r } = await api.getIntegrationRequests({
        type: filterType || undefined,
        status: filterStatus || undefined,
      });
      setRequests(r);
    } catch (err) {
      console.error('Failed to load requests:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, [filterType, filterStatus]);

  const [rejectModal, setRejectModal] = useState<{ id: number } | null>(null);
  const [rejectComment, setRejectComment] = useState('');
  const [rejecting, setRejecting] = useState(false);

  const updateStatus = async (id: number, status: string, comment?: string) => {
    try {
      const { request } = await api.updateIntegrationRequestStatus(id, status, comment);
      setRequests((prev) => prev.map((r) => (r.id === id ? request : r)));
      setRejectModal(null);
      setRejectComment('');
    } catch (err) {
      console.error('Failed to update status:', err);
      throw err;
    }
  };

  const handleReject = async () => {
    if (!rejectModal || !rejectComment.trim()) return;
    setRejecting(true);
    try {
      await updateStatus(rejectModal.id, 'rejected', rejectComment.trim());
    } finally {
      setRejecting(false);
    }
  };

  const [changeMenuOpen, setChangeMenuOpen] = useState<number | null>(null);
  const changeMenuRef = useRef<HTMLDivElement | null>(null);

  // Last request per store (API returns created_at DESC, so first occurrence = latest)
  const lastByStore = (() => {
    const map = new Map<string, IntegrationRequest>();
    for (const r of requests) {
      if (!map.has(r.store_id)) map.set(r.store_id, r);
    }
    return map;
  })();
  const isLastForStore = (r: IntegrationRequest) => lastByStore.get(r.store_id)?.id === r.id;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (changeMenuRef.current && !changeMenuRef.current.contains(e.target as Node)) {
        setChangeMenuOpen(null);
      }
    };
    if (changeMenuOpen !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [changeMenuOpen]);

  return (
    <div className="container mx-auto px-6 py-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Plug className="w-5 h-5" />
              Заявки на интеграцию
            </CardTitle>
            <div className="flex gap-2">
              <select
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="">Все типы</option>
                <option value="yandex_eda">Яндекс.Еда</option>
                <option value="wolt">Wolt</option>
                <option value="halyk_market">Халык маркет</option>
                <option value="glovo">Glovo</option>
              </select>
              <select
                className="h-9 rounded-md border border-input bg-background px-3 text-sm"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">Все статусы</option>
                <option value="pending">Ожидает</option>
                <option value="approved">Одобрено</option>
                <option value="rejected">Отклонено</option>
              </select>
              <Button variant="outline" size="sm" onClick={loadRequests} disabled={isLoading}>
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : requests.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">Нет заявок</p>
            ) : (
              <div className="space-y-4">
                {requests.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between p-4 rounded-lg border bg-background"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">
                          {INTEGRATION_LABELS[r.integration_type] || r.integration_type}
                        </span>
                        <Badge variant={STATUS_VARIANTS[r.status] || 'secondary'}>
                          {STATUS_LABELS[r.status] || r.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        Магазин:{' '}
                        {ADMIN_CABINET_URL ? (
                          <a
                            href={shopUrl(r.store_id, '/general')}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline"
                          >
                            {r.store_name || r.store_id}
                          </a>
                        ) : (
                          (r.store_name || r.store_id)
                        )}
                        {r.user_email && ` • ${r.user_email}`}
                        {!r.user_email && r.pimmer_user_id && ` • Пользователь: ${r.pimmer_user_id}`}
                      </p>
                      {r.status === 'rejected' && r.rejection_comment && (
                        <p className="text-sm text-destructive mt-1">
                          Причина отклонения: {r.rejection_comment}
                        </p>
                      )}
                      {r.integration_type === 'yandex_eda' && r.settings && (
                        <p className="text-sm text-muted-foreground mt-0.5">
                          Наценка: {(1 + (r.settings.markupPercent ?? 0) / 100).toLocaleString('ru-RU', { minimumFractionDigits: 2 })}
                          {r.settings.active !== false && ' • Интеграция активна'}
                        </p>
                      )}
                      {r.integration_type === 'yandex_eda' && (() => {
                        const meta = parseMetadata(r);
                        if (!meta) return null;
                        return (
                          <div className="mt-2 space-y-1 text-sm">
                            <p className="text-muted-foreground">
                              {ADMIN_CABINET_URL ? (
                                <a
                                  href={shopUrl(r.store_id, '/products')}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline"
                                >
                                  Товаров: {meta.product_count ?? '—'}
                                </a>
                              ) : (
                                <>Товаров: {meta.product_count ?? '—'}</>
                              )}
                              {' • '}Демо: {meta.demo_products_removed ? 'удалены' : 'есть'}
                              {meta.store_code && (
                                <>
                                  {' • '}
                                  {ADMIN_CABINET_URL ? (
                                    <a
                                      href={shopUrl(r.store_id, '/additional-settings')}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-primary hover:underline"
                                    >
                                      Код: {meta.store_code}
                                    </a>
                                  ) : (
                                    <>Код: {meta.store_code}</>
                                  )}
                                </>
                              )}
                            </p>
                            <p className="text-muted-foreground">
                              Шаги:{' '}
                              {ADMIN_CABINET_URL && meta.launch_steps?.product_import ? (
                                <a
                                  href={shopUrl(r.store_id, '/import')}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-primary hover:underline"
                                >
                                  Импорт ✓
                                </a>
                              ) : (
                                <>Импорт {meta.launch_steps?.product_import ? '✓' : '✗'}</>
                              )}
                              {' • '}Договор {meta.launch_steps?.yandex_contract ? '✓' : '✗'}
                            </p>
                          </div>
                        );
                      })()}
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatDate(r.created_at)}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2 items-end">
                      {ADMIN_CABINET_URL && (
                        <a
                          href={shopUrl(r.store_id, '/integrations/yandex_shops')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex h-9 items-center justify-center rounded-md border border-input bg-background px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Перейти в интеграцию
                        </a>
                      )}
                      {r.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="default"
                            onClick={() => updateStatus(r.id, 'approved')}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Одобрить
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setRejectModal({ id: r.id })}
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Отклонить
                          </Button>
                        </div>
                      )}
                      {isLastForStore(r) && (r.status === 'approved' || r.status === 'rejected') && (
                        <div className="relative" ref={changeMenuOpen === r.id ? changeMenuRef : null}>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setChangeMenuOpen(changeMenuOpen === r.id ? null : r.id)}
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                          {changeMenuOpen === r.id && (
                            <div className="absolute right-0 top-full mt-1 z-20 bg-card border rounded-md shadow-lg py-1 min-w-[140px]">
                              {r.status === 'approved' && (
                                <button
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 text-destructive"
                                  onClick={() => {
                                    setRejectModal({ id: r.id });
                                    setChangeMenuOpen(null);
                                  }}
                                >
                                  <XCircle className="w-4 h-4" />
                                  Отклонить
                                </button>
                              )}
                              {r.status === 'rejected' && (
                                <button
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2"
                                  onClick={() => {
                                    updateStatus(r.id, 'approved');
                                    setChangeMenuOpen(null);
                                  }}
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  Одобрить
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {rejectModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4">
            <Card className="w-full max-w-md">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Отклонить заявку</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => { setRejectModal(null); setRejectComment(''); }}>
                  Закрыть
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reject-comment">Причина отклонения (обязательно)</Label>
                  <Input
                    id="reject-comment"
                    placeholder="Укажите причину отклонения заявки"
                    value={rejectComment}
                    onChange={(e) => setRejectComment(e.target.value)}
                    disabled={rejecting}
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    onClick={handleReject}
                    disabled={!rejectComment.trim() || rejecting}
                  >
                    {rejecting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Отклонить'}
                  </Button>
                  <Button variant="outline" onClick={() => { setRejectModal(null); setRejectComment(''); }}>
                    Отмена
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
    </div>
  );
}
